import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plugs, ArrowsClockwise, LinkBreak, CheckCircle, Buildings, FacebookLogo, AirplaneTakeoff, CaretDown, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { api, formatApiErrorDetail } from "@/lib/api";

const STEPS = [
  { t: "Check the 5 requirements →", d: "They decide whether the connection will work. 90% of failed connections are just the wrong Facebook account." },
  { t: "Continue with Facebook", d: "Log in and approve every permission Facebook shows. Skipping one silently breaks lead sync or campaign actions." },
  { t: "Pick your ad accounts", d: "Your login may access several accounts — tick only the ones 5ads.ai should manage." },
  { t: "Done — data flows in", d: "Campaigns, ads and leads appear within a minute. All write actions (pause / scale / publish) still require your approval." },
];

const CHECKS = [
  { t: "I'm logging in with the Facebook account that manages our Business Manager",
    d: "Not your personal profile — the account that owns/administers the business." },
  { t: "Our ads run from an ad account inside that Business Manager",
    d: "You launch ads from this portal — a profile with no ad account will connect but stay empty." },
  { t: "I'm an admin of the Facebook Page running our lead forms",
    d: "Required by Meta to hand over leads. Editor/Analyst roles are not enough." },
  { t: "Our business is verified by Meta (blue \u201cVerified\u201d badge)",
    d: "Check in Meta Business Suite \u2192 Settings \u2192 Business portfolio info \u2192 Business verification status. It must look like this:", verified: true },
  { t: "I'll grant ALL permissions Facebook asks for",
    d: "Every scope is needed: ads_read, ads_management, leads_retrieval, pages access." },
];

const VerifiedSample = () => (
  <div className="mt-2 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2" data-testid="verified-badge-sample">
    <div className="text-[10px] font-bold text-[#525866]">Business verification status</div>
    <div className="flex items-center gap-1.5 mt-1">
      <span className="h-3.5 w-3.5 rounded-full bg-[#1877F2] text-white text-[9px] font-extrabold flex items-center justify-center">✓</span>
      <span className="text-xs font-extrabold text-[#0A0D14]">Verified</span>
      <span className="text-[10px] text-[#8a90a0]">· Your Business Pvt Ltd</span>
    </div>
  </div>
);

const GateModal = ({ open, onClose, onConfirm, busy }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-md" data-testid="meta-gate-modal">
      <DialogHeader>
        <DialogTitle className="font-heading text-lg font-extrabold">Before we send you to Facebook…</DialogTitle>
        <DialogDescription className="text-xs">Quick sanity check — if any of these is false, the connection will fail or stay empty:</DialogDescription>
      </DialogHeader>
      <div className="space-y-2.5 text-[12.5px] leading-relaxed">
        {["You'll log in as the Business Manager admin account (the one your ads run from)",
          "That business has an active ad account you launch ads from",
          "You're an admin of the Facebook Page running your lead forms",
          "Your business is verified by Meta — blue ✓ badge in Business Suite → Business portfolio info",
          "On the consent screen you'll press \u201cAllow\u201d on every permission — no unticking"].map((r, i) => (
          <div key={i} className="flex gap-2.5">
            <span className="text-[#0052FF] font-extrabold shrink-0">{["①", "②", "③", "④", "⑤"][i]}</span>
            <span className="text-[#3a4150]">{r}</span>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[11.5px] text-red-700 leading-relaxed">
        <Warning size={13} weight="fill" className="inline mr-1 -mt-0.5" />
        Most common failure: logging in with a personal account that doesn't own the Business Manager. Leads and campaigns will simply not appear.
      </div>
      <div className="flex gap-2.5">
        <Button variant="outline" onClick={onClose} data-testid="meta-gate-back" className="flex-1 rounded-full font-bold text-xs h-11">Go back</Button>
        <Button onClick={onConfirm} disabled={busy} data-testid="meta-gate-confirm"
          className="flex-[2] rounded-full bg-[#1877F2] hover:bg-[#145fc4] text-white font-bold text-xs h-11">
          {busy ? "Opening Facebook…" : "I meet these — take me to Facebook →"}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

export default function Connect() {
  const [status, setStatus] = useState(null);
  const [oauthCfg, setOauthCfg] = useState(null);
  const [form, setForm] = useState({ app_id: "", app_secret: "", access_token: "" });
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [checks, setChecks] = useState([]);
  const [gateOpen, setGateOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  const loadStatus = useCallback(() => {
    api.get("/meta/status").then(({ data }) => {
      setStatus(data);
      if (data.connected) setSelected(data.enabled_account_ids || []);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    api.get("/meta/oauth/config").then(({ data }) => setOauthCfg(data)).catch(() => setOauthCfg({ available: false }));
  }, []);

  useEffect(() => {
    const meta = params.get("meta");
    if (!meta) return;
    if (meta === "connected") toast.success("Facebook connected! Choose which ad accounts to manage, then sync.", { duration: 8000 });
    else if (meta === "cancelled") toast.warning("Facebook connection was cancelled.");
    else toast.error(params.get("reason") || "Facebook connection failed", { duration: 9000 });
    setParams({}, { replace: true });
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startOauth = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/meta/oauth/start", { params: { return_to: "/connect" } });
      window.location.assign(data.authorization_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/meta/sync");
      toast.success(`Synced ${data.accounts} accounts, ${data.campaigns} campaigns, ${data.ads} ads, ${data.leads} leads`);
      (data.warnings || []).forEach((w) => toast.warning(w, { duration: 9000 }));
      loadStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/meta/connect", form);
      toast.success(`Connected! ${data.accounts.length} ad account(s) found. Now choose which accounts to manage, then sync.`, { duration: 8000 });
      loadStatus();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail), { duration: 9000 });
    } finally {
      setBusy(false);
    }
  };

  const toggleAccount = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const saveAccounts = async () => {
    if (selected.length === 0) {
      toast.error("Select at least one ad account");
      return;
    }
    try {
      await api.put("/meta/accounts", { account_ids: selected });
      toast.success(`Managing ${selected.length} account(s). Syncing…`);
      await runSync();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const disconnect = async () => {
    try {
      await api.delete("/meta/disconnect");
      toast.success("Disconnected — synced workspace data cleared. Re-connect anytime to restore it.");
      setConfirmDisconnect(false);
      loadStatus();
    } catch (e) {
      toast.error("Disconnect failed");
    }
  };

  const toggleCheck = (i) => setChecks((c) => (c.includes(i) ? c.filter((x) => x !== i) : [...c, i]));
  const allChecked = checks.length === CHECKS.length;
  const connected = status?.connected;

  const expDays = status?.token_expires_at
    ? Math.ceil((new Date(status.token_expires_at) - Date.now()) / 86400000) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl">
      <div>
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0A0D14]">Connect your Meta ad account</h2>
        <p className="text-sm text-[#525866] mt-2 leading-relaxed">
          One Facebook login links your ad accounts, campaigns, creatives and lead forms. From then on 5ads.ai syncs automatically — nightly full sync + new-lead check every 5 minutes.
        </p>
        <div className="mt-8 space-y-5">
          {STEPS.map((s, i) => (
            <div key={i} className="flex gap-4">
              <span className="h-7 w-7 shrink-0 rounded-full bg-[#0A0D14] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <div>
                <div className="font-heading text-sm font-extrabold text-[#0A0D14]">{s.t}</div>
                <p className="text-xs text-[#525866] leading-relaxed mt-1">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        {connected ? (
          <div className="bg-white border border-success/30 rounded-lg p-6" data-testid="meta-connected-card">
            <div className="flex items-center gap-2">
              <CheckCircle size={22} weight="fill" className="text-success" />
              <h3 className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]">Meta API Connected</h3>
              <Badge className="ml-auto bg-success/10 text-[#00953e] border border-success/25 rounded-full font-bold text-[10px]">LIVE</Badge>
            </div>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#8a90a0]">Connected via</span><span className="font-bold text-[#0A0D14]">{status.connected_via === "oauth" ? "Facebook Login" : "Manual credentials"}</span></div>
              <div className="flex justify-between"><span className="text-[#8a90a0]">App ID</span><span className="font-mono text-[#0A0D14]">{status.app_id}</span></div>
              <div className="flex justify-between"><span className="text-[#8a90a0]">Managed accounts</span><span className="font-bold text-[#0A0D14]">{status.account_count} of {(status.available_accounts || []).length} accessible</span></div>
              <div className="flex justify-between"><span className="text-[#8a90a0]">Last sync</span><span className="text-[#0A0D14]">{status.last_sync ? new Date(status.last_sync).toLocaleString() : "Never"}</span></div>
              <div className="flex justify-between"><span className="text-[#8a90a0]">Token expires</span>
                <span className={expDays != null && expDays <= 10 ? "font-bold text-[#b07600]" : "text-[#0A0D14]"}>
                  {status.token_expires_at ? `${new Date(status.token_expires_at).toLocaleDateString()}${expDays != null ? ` (${expDays <= 0 ? "expired" : `${expDays}d left`})` : ""}` : "—"}
                </span>
              </div>
            </div>
            {oauthCfg?.available && expDays != null && expDays <= 10 && (
              <Button onClick={startOauth} disabled={busy} data-testid="meta-reconnect-button"
                className="mt-4 w-full h-10 rounded-full bg-[#1877F2] hover:bg-[#145fc4] text-white font-bold text-xs">
                <FacebookLogo size={16} weight="fill" className="mr-1.5" /> {busy ? "Opening Facebook…" : "Re-connect with Facebook (renews token)"}
              </Button>
            )}
            <div className="mt-5 pt-4 border-t border-[#E5E7EB]" data-testid="meta-account-selection">
              <div className="flex items-center gap-2 mb-1">
                <Buildings size={15} weight="duotone" className="text-[#0052FF]" />
                <span className="font-heading text-sm font-extrabold text-[#0A0D14]">Which ad accounts should 5ads.ai manage?</span>
              </div>
              <p className="text-[11px] text-[#8a90a0] mb-3">Your token can access all accounts below. Only ticked accounts are synced and shown in the app.</p>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {(status.available_accounts || []).map((a) => (
                  <label key={a.id} data-testid={`account-checkbox-${a.id}`}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                      selected.includes(a.id) ? "border-[#0052FF]/40 bg-[#0052FF]/[0.04]" : "border-[#E5E7EB] hover:border-[#c9cdd6]"}`}>
                    <Checkbox checked={selected.includes(a.id)} onCheckedChange={() => toggleAccount(a.id)} />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-[#0A0D14] truncate">{a.name}</span>
                      <span className="block text-[10px] font-mono text-[#8a90a0]">{a.id}</span>
                    </span>
                  </label>
                ))}
              </div>
              <Button onClick={saveAccounts} disabled={syncing || selected.length === 0} data-testid="meta-save-accounts-button"
                className="mt-3 w-full rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-10">
                {syncing ? "Syncing…" : `Save Selection & Sync (${selected.length})`}
              </Button>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={runSync} disabled={syncing} data-testid="meta-sync-button"
                className="flex-1 rounded-full bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold transition-transform hover:-translate-y-[1px] active:scale-[0.98]">
                <ArrowsClockwise size={16} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync Now"}
              </Button>
              <Button onClick={() => setConfirmDisconnect(true)} variant="outline" data-testid="meta-disconnect-button"
                className="rounded-full border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 font-bold">
                <LinkBreak size={16} className="mr-1.5" /> Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {oauthCfg?.available ? (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="meta-preflight-card">
                <div className="flex items-center gap-2">
                  <AirplaneTakeoff size={19} weight="duotone" className="text-[#0052FF]" />
                  <h3 className="font-heading text-base font-extrabold tracking-tight text-[#0A0D14]">Before you connect — all 5 must be true</h3>
                </div>
                <div className="mt-3 space-y-2">
                  {CHECKS.map((c, i) => (
                    <label key={i} data-testid={`preflight-check-${i}`}
                      className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 cursor-pointer transition-colors ${
                        checks.includes(i) ? "border-[#0052FF]/40 bg-[#0052FF]/[0.04]" : "border-[#E5E7EB] hover:border-[#c9cdd6]"}`}>
                      <Checkbox checked={checks.includes(i)} onCheckedChange={() => toggleCheck(i)} className="mt-0.5" />
                      <span className="min-w-0">
                        <span className="block text-[12.5px] font-bold text-[#0A0D14] leading-snug">{c.t}</span>
                        <span className="block text-[11px] text-[#8a90a0] leading-relaxed mt-0.5">{c.d}</span>
                        {c.verified && <VerifiedSample />}
                      </span>
                    </label>
                  ))}
                </div>
                <Button onClick={() => setGateOpen(true)} disabled={!allChecked || busy} data-testid="meta-oauth-button"
                  className={`mt-4 w-full h-11 rounded-full font-bold text-sm text-white transition-transform ${
                    allChecked ? "bg-[#1877F2] hover:bg-[#145fc4] hover:-translate-y-[1px] active:scale-[0.98]" : "bg-[#c9cdd6]"}`}>
                  <FacebookLogo size={18} weight="fill" className="mr-2" /> Continue with Facebook
                </Button>
                <p className="text-center text-[10.5px] text-[#8a90a0] mt-2">Button unlocks when all 5 boxes are ticked</p>
              </div>
            ) : oauthCfg && (
              <div className="rounded-lg border border-dashed border-[#c9cdd6] bg-white px-4 py-3 text-[11px] text-[#8a90a0] leading-relaxed" data-testid="meta-oauth-unavailable-note">
                <span className="font-bold text-[#525866]">One-click Facebook connect (for platform owner):</span> set <span className="font-mono">META_APP_ID</span> and <span className="font-mono">META_APP_SECRET</span> in the backend .env, add the Facebook Login product to your Meta app, and whitelist this OAuth redirect URI:{" "}
                <span className="font-mono break-all text-[#0A0D14]">{oauthCfg.redirect_uri || "(set PUBLIC_BASE_URL)"}</span>
              </div>
            )}
            <Collapsible open={advancedOpen || !oauthCfg?.available} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <button data-testid="meta-advanced-toggle"
                  className="w-full flex items-center justify-between rounded-lg border border-dashed border-[#c9cdd6] bg-white px-4 py-3 text-xs text-[#8a90a0] hover:border-[#8a90a0] transition-colors">
                  <span><span className="font-bold text-[#525866]">Advanced (for developers):</span> connect with App ID, Secret &amp; System-User token instead</span>
                  <CaretDown size={13} weight="bold" className={`transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <form onSubmit={connect} className="mt-3 bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-5" data-testid="meta-connect-form">
                  <div className="flex items-center gap-2">
                    <span className="h-8 w-8 rounded-lg bg-[#0052FF]/10 flex items-center justify-center"><Plugs size={18} weight="duotone" className="text-[#0052FF]" /></span>
                    <h3 className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]">Your Meta credentials</h3>
                  </div>
                  <p className="text-[11px] text-[#8a90a0] leading-relaxed -mt-2">
                    For developers: create a Meta app at developers.facebook.com, copy App ID &amp; Secret from Settings → Basic, and generate a token in Graph API Explorer (or use a never-expiring Business Manager System-User token) with ads_read, ads_management, leads_retrieval, pages_show_list, pages_read_engagement.
                  </p>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525866]">App ID</Label>
                    <Input data-testid="meta-appid-input" required value={form.app_id}
                      onChange={(e) => setForm({ ...form, app_id: e.target.value.trim() })}
                      placeholder="e.g. 1234567890123456" className="h-11 rounded-lg font-mono text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525866]">App Secret</Label>
                    <Input data-testid="meta-secret-input" required type="password" value={form.app_secret}
                      onChange={(e) => setForm({ ...form, app_secret: e.target.value.trim() })}
                      placeholder="••••••••••••••••" className="h-11 rounded-lg font-mono text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525866]">Access Token</Label>
                    <Input data-testid="meta-token-input" required value={form.access_token}
                      onChange={(e) => setForm({ ...form, access_token: e.target.value.trim() })}
                      placeholder="EAAB… (from Graph API Explorer)" className="h-11 rounded-lg font-mono text-sm" />
                    <p className="text-[11px] text-[#8a90a0]">Exchanged server-side for a long-lived token. Never exposed to the browser after this.</p>
                  </div>
                  <Button type="submit" disabled={busy || syncing} data-testid="meta-connect-button"
                    className="w-full h-11 rounded-full bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold transition-transform hover:-translate-y-[1px] active:scale-[0.98]">
                    {busy ? "Connecting…" : syncing ? "Syncing data…" : "Connect & Sync"}
                  </Button>
                </form>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
        <div className="mt-4 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-xs text-[#8a6400] leading-relaxed">
          Write actions (pause / resume / scale) require the <span className="font-mono font-bold">ads_management</span> permission. Lead retrieval requires <span className="font-mono font-bold">leads_retrieval</span> and Page admin access. Tokens are stored only in your backend database.
        </div>
      </div>

      <GateModal open={gateOpen} onClose={() => setGateOpen(false)} onConfirm={startOauth} busy={busy} />

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent data-testid="meta-disconnect-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-extrabold text-red-600">Disconnect Meta?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-xs text-[#525866] space-y-2.5">
                <p>This clears all synced data from this workspace:</p>
                <div className="space-y-1.5">
                  <div className="flex gap-2"><span className="text-red-500 font-bold">✕</span> Campaigns, ad sets, ads &amp; performance history</div>
                  <div className="flex gap-2"><span className="text-red-500 font-bold">✕</span> All leads and their statuses</div>
                  <div className="flex gap-2"><span className="text-red-500 font-bold">✕</span> Scheduled syncs, WhatsApp auto-replies &amp; AI agent runs</div>
                </div>
                <p className="text-[#8a90a0]">Your Facebook account and live campaigns on Meta are <b>not</b> affected. Re-connecting restores everything on the next sync.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="meta-disconnect-cancel" className="rounded-full font-bold text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={disconnect} data-testid="meta-disconnect-confirm"
              className="rounded-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs">
              Yes, disconnect &amp; clear data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
